import { useCallback, useMemo } from "react";
import type { SaveData } from "../components/SaveProgressModal/types";
import type { FilePathResult } from "../types/save";
import { resolveFilePath } from "../services/pathResolverService";
import type { OrderSavePayload } from "../services/pathResolverService";
import { sendToTerminal } from "../lib/terminalLogger";

export type PathResolverOptions = {
	useCVIfVariavel?: boolean;
};

export type PathResolverResult = FilePathResult & {
	storageMethod?: "smb" | "gdrive";
};

/**
 * Hook que resolve paths de salvamento via backend (POST /path/resolve).
 *
 * Substitui a lógica local do useFilePathBuilder para o fluxo order_save.
 * O caller deve tratar erros e usar fallback local se necessário.
 */
export function usePathResolver() {
	const resolvePath = useCallback(
		async (
			data: SaveData,
			role: string,
			opts: PathResolverOptions = {}
		): Promise<PathResolverResult> => {
			if (!data?.pedido?.orc_id || !data?.pedido?.oit_id) {
				return {
					filePath: null,
					fileName: null,
					error: "orc_id ou oit_id ausente",
				};
			}

			// Contração
			const contraction =
				opts.useCVIfVariavel &&
				(data as any).configuracoes?.contracoesVariaveis
					? "CV"
					: (data as any).configuracoes?.contracao || "0px";

			const payload: OrderSavePayload = {
				flow: "order_save",
				orc_id: Number(data.pedido.orc_id),
				oit_id: Number(data.pedido.oit_id),
				role,
				qtdObjetos:
					(data as any).totalObjetos ||
					data.objetos?.length ||
					1,
				contraction,
				delivery:
					(data as any).configuracoes?.entrega || "Hoje",
			};

			sendToTerminal("📂 [PATH_RESOLVER] Chamando backend", {
				orc_id: payload.orc_id,
				oit_id: payload.oit_id,
				role,
			});

			const resolved = await resolveFilePath(payload);

			return {
				filePath: resolved.smb.fullPath,
				fileName: resolved.smb.fileName,
				storageMethod: resolved.metadata.storageMethod,
				error: null,
			};
		},
		[]
	);

	return useMemo(() => ({ resolvePath }), [resolvePath]);
}
